import { TypedDocumentNode, gql } from '@apollo/client';
import {
  faCardsBlank,
  faPen,
  faTrash,
  faUser,
} from '@fortawesome/pro-solid-svg-icons';
import { useState } from 'react';
import { FormattedMessage, FormattedNumber, defineMessages } from 'react-intl';
import { Link, generatePath, useParams } from 'react-router-dom';
import styled from 'styled-components';

import { Rarity, Sport } from '@sorare/core/src/__generated__/globalTypes';
import Button from '@sorare/core/src/atoms/buttons/Button';
import ButtonBase from '@sorare/core/src/atoms/buttons/ButtonBase';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import ScarcityIcon from '@sorare/core/src/atoms/icons/ScarcityIcon';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import { GridOverflowX } from '@sorare/core/src/atoms/layout/grid';
import { HeadlineXL, Text14, Text16 } from '@sorare/core/src/atoms/typography';
import Bold from '@sorare/core/src/atoms/typography/Bold';
import { ButtonBaseWithConfirmDialog } from '@sorare/core/src/components/form/ButtonWithConfirmDialog';
import { SEARCH_PARAMS } from '@sorare/core/src/components/search/InstantSearch/types';
import { buildFilterQuery } from '@sorare/core/src/components/search/InstantSearch/utils';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { isType } from '@sorare/core/src/gql';
import idFromObject from '@sorare/core/src/gql/idFromObject';
import useDecks from '@sorare/core/src/hooks/decks/useDecks';
import useUpdateSportProfile from '@sorare/core/src/hooks/useUpdateSportProfile';
import {
  blockchainRarities,
  playableBlockchainRarities,
} from '@sorare/core/src/lib/cards';
import { navLabels } from '@sorare/core/src/lib/glossary';

import { FlexCard } from '@sorare/marketplace/src/components/token/FlexCard';

import { CardsGrid } from '../CardsGrid';
import { Share, TABS } from '../Header/Share';
import { ProfileCta } from '../ProfileCta';
import { ChooseDeck } from './ChooseDeck';
import {
  Gallery_anyCard,
  Gallery_cardCount,
  Gallery_cardCountsByRarity,
  Gallery_deck,
  Gallery_socialPictureDerivative,
} from './__generated__/index.graphql';

const Root = styled(Vertical).attrs({ gap: 3 })`
  border-radius: var(--double-unit);
  background-color: var(--c-neutral-200);
  position: relative;
  overflow: hidden;

  &:before {
    content: '';
    position: absolute;
    inset: 0;
    z-index: 1;
    border-radius: var(--double-unit);
    background: linear-gradient(
      180deg,
      rgba(var(--c-rgb-neutral-200), 1) 2%,
      rgba(var(--c-rgb-neutral-200), 0.6) 30%,
      rgba(var(--c-rgb-neutral-200), 0.6) 70%,
      rgba(var(--c-rgb-neutral-200), 1) 98%
    );
  }
`;

const Cards = styled(GridOverflowX)`
  grid-template-columns: repeat(5, 140px);
`;
const FakeCardRoot = styled(Horizontal).attrs({ gap: 0, center: true })`
  width: 100%;
  aspect-ratio: var(--card-aspect-ratio);
  border-radius: var(--double-unit);
  background: var(--c-neutral-300);
`;

const StyledCardsGrid = styled(CardsGrid)`
  border-radius: var(--double-unit);
  z-index: 0;
`;

const Header = styled(SBHorizontal)`
  width: 100%;
`;

const Content = styled(Vertical).attrs({ gap: 3, center: true })`
  position: relative;
  z-index: 2;
  min-height: 330px;
  padding: var(--intermediate-unit);
`;

export type Props = {
  sport: Sport;
  cards: Gallery_anyCard[];
  galleryUrl: string;
  cardCount: Gallery_cardCount | Gallery_cardCountsByRarity;
  highlightedDeck?: Gallery_deck | null;
  onAddDeck: () => void;
  onEditDeck: (slug: string) => void;
  managerProfilePath: string;
  managerProfileSocialPictures?: Gallery_socialPictureDerivative;
  galleryPath: string;
  gallerySocialPictures?: Gallery_socialPictureDerivative;
  highlightedDeckPath?: string;
  highlightedDeckSocialPictures?: Gallery_socialPictureDerivative;
};

const FakeCard = () => {
  return (
    <FakeCardRoot>
      <FontAwesomeIcon icon={faUser} />
    </FakeCardRoot>
  );
};

const Card = ({ card }: { card: Gallery_anyCard }) => {
  return <FlexCard withLink card={card} />;
};

const messages = defineMessages({
  cards: {
    id: 'ManagerProfile.Gallery.cards',
    defaultMessage: 'Cards',
  },
  seeCards: {
    id: 'ManagerProfile.Gallery.seeCards',
    defaultMessage: 'See cards',
  },
  seeMyCards: {
    id: 'ManagerProfile.Gallery.seeMyCards',
    defaultMessage: 'See my cards',
  },
});

const ScarcityCount = ({
  galleryUrl,
  slug,
  scarcity,
  count,
}: {
  galleryUrl: string;
  slug?: string;
  scarcity: (typeof blockchainRarities)[number];
  count: number;
}) => {
  return (
    <Link
      key={scarcity}
      to={`${generatePath(galleryUrl, {
        slug,
      })}?${buildFilterQuery({
        [SEARCH_PARAMS.RARITY]: scarcity,
      })}`}
    >
      <Horizontal>
        <ScarcityIcon scarcity={scarcity as Rarity} />
        <Text14 bold color="var(--c-static-neutral-500)">
          <FormattedNumber value={count} />
        </Text14>
      </Horizontal>
    </Link>
  );
};

export const Gallery = ({
  sport,
  cards,
  galleryUrl,
  cardCount,
  highlightedDeck,
  onAddDeck,
  onEditDeck,
  managerProfilePath,
  managerProfileSocialPictures,
  galleryPath,
  gallerySocialPictures,
  highlightedDeckPath,
  highlightedDeckSocialPictures,
}: Props) => {
  const { slug } = useParams();
  const { currentUser } = useCurrentUserContext();
  const updateSportProfile = useUpdateSportProfile();
  const { decks, loading } = useDecks({ sport });
  const [open, setOpen] = useState(false);

  const hasHighlightedDeck =
    highlightedDeck && highlightedDeck?.cards?.nodes.length > 0;

  const cardsToDisplay = hasHighlightedDeck
    ? highlightedDeck.cards.nodes
    : cards;

  const nbCardsToDisplay = 5;
  const nbCardsMissing = Math.max(nbCardsToDisplay - cardsToDisplay.length, 0);

  const isMe = currentUser?.slug === slug;

  const onEditDeckClick = () => {
    if (!loading && !decks.length) {
      onAddDeck();
    }
    setOpen(true);
  };

  return (
    <>
      <Root>
        {hasHighlightedDeck && <StyledCardsGrid cards={cards} />}
        <Content center>
          <Header>
            <Horizontal>
              <FontAwesomeIcon icon={faCardsBlank} />
              <Text16 bold>
                <FormattedMessage
                  {...(isMe ? navLabels.myCards : messages.cards)}
                />
              </Text16>
            </Horizontal>
            <Horizontal>
              {playableBlockchainRarities.map(scarcity => {
                return (
                  <ScarcityCount
                    key={scarcity}
                    galleryUrl={galleryUrl}
                    slug={slug}
                    scarcity={scarcity}
                    count={cardCount[scarcity]}
                  />
                );
              })}
              {isType(cardCount, 'CardCount') && (
                <ScarcityCount
                  galleryUrl={galleryUrl}
                  slug={slug}
                  scarcity={Rarity.custom_series}
                  count={cardCount[Rarity.custom_series]}
                />
              )}
            </Horizontal>
          </Header>
          {hasHighlightedDeck && (
            <Horizontal gap={2}>
              <HeadlineXL as="h3">{highlightedDeck.name}</HeadlineXL>
              {isMe && (
                <>
                  <ButtonBase onClick={onEditDeckClick}>
                    <FontAwesomeIcon icon={faPen} />
                  </ButtonBase>
                  <ButtonBaseWithConfirmDialog
                    onConfirm={() => {
                      updateSportProfile({ sport, highlightedDeckId: '' });
                    }}
                    message={
                      <span>
                        <FormattedMessage
                          id="DeckEditorDialog.areYouSureStopHighlight"
                          defaultMessage="Are you sure you want to stop highlighting <b>{list}</b>?"
                          values={{ list: highlightedDeck.name, b: Bold }}
                        />
                      </span>
                    }
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </ButtonBaseWithConfirmDialog>
                </>
              )}
            </Horizontal>
          )}
          <Cards
            style={{
              gridTemplateColumns: hasHighlightedDeck
                ? `repeat(${highlightedDeck.cards.nodes.length}, 140px)`
                : '',
            }}
          >
            {cardsToDisplay.slice(0, nbCardsToDisplay).map(card => (
              <Card key={card.slug} card={card} />
            ))}
            {!hasHighlightedDeck &&
              [...Array(nbCardsMissing)].map((_, index) => (
                // eslint-disable-next-line react/no-array-index-key
                <FakeCard key={index} />
              ))}
          </Cards>
          <Horizontal>
            <ProfileCta to={galleryUrl}>
              <FormattedMessage
                {...(isMe ? messages.seeMyCards : messages.seeCards)}
              />
            </ProfileCta>
            {hasHighlightedDeck ? (
              <Share
                defaultTab={TABS.HIGHLIGHTED_DECK}
                managerProfilePath={managerProfilePath}
                managerProfileSocialPictures={managerProfileSocialPictures}
                galleryPath={galleryPath}
                gallerySocialPictures={gallerySocialPictures}
                highlightedDeckPath={highlightedDeckPath}
                highlightedDeckSocialPictures={highlightedDeckSocialPictures}
              />
            ) : (
              isMe && (
                <Button
                  size="medium"
                  onClick={onEditDeckClick}
                  color="quaternary"
                >
                  <FormattedMessage
                    id="ManagerProfile.HallOfFame.pickOtherCards"
                    defaultMessage="Edit highlight"
                  />
                </Button>
              )
            )}
          </Horizontal>
        </Content>
      </Root>
      {open && (
        <ChooseDeck
          highlightedDeckId={highlightedDeck?.id}
          sport={sport}
          open
          onClose={() => setOpen(false)}
          onSubmit={deckId => {
            updateSportProfile({
              sport,
              highlightedDeckId: idFromObject(deckId),
            });
          }}
          onAddDeck={onAddDeck}
          onEditDeck={onEditDeck}
        />
      )}
    </>
  );
};
const cardFragment = gql`
  fragment Gallery_anyCard on AnyCardInterface {
    slug
    rarityTyped
    pictureUrl
    ...FlexCard_anyCard
  }
  ${FlexCard.fragments.anyCard}
` as TypedDocumentNode<Gallery_anyCard>;

Gallery.fragments = {
  anyCard: cardFragment,
  cardCount: gql`
    fragment Gallery_cardCount on CardCount {
      limited
      rare
      super_rare: superRare
      unique
      custom_series: customSeries
    }
  ` as TypedDocumentNode<Gallery_cardCount>,
  deck: gql`
    fragment Gallery_deck on Deck {
      id
      slug
      name
      cards(first: 5) {
        nodes {
          slug
          ...Gallery_anyCard
        }
      }
    }
    ${cardFragment}
  ` as TypedDocumentNode<Gallery_deck>,
  cardCountsByRarity: gql`
    fragment Gallery_cardCountsByRarity on CardCountsByRarity {
      unique: uniqueCount
      super_rare: superRareCount
      rare: rareCount
      limited: limitedCount
    }
  ` as TypedDocumentNode<Gallery_cardCountsByRarity>,
  socialPictureDerivative: gql`
    fragment Gallery_socialPictureDerivative on SocialPictureDerivative {
      ...Share_socialPictureDerivative
    }
    ${Share.fragments.socialPictureDerivative}
  ` as TypedDocumentNode<Gallery_socialPictureDerivative>,
};
